<template>
  <v-dialog
    v-model="isOpen"
    max-width="430"
    persistent
    transition="fade-transition"
    @click:outside="handleCancel"
  >
    <v-card class="confirmation-modal">
      <!-- Content -->
      <div class="modal-content">
        <div class="modal-text">
          <h3 class="modal-title">{{ title }}</h3>
          <p class="modal-description">{{ description }}</p>
        </div>
      </div>

      <!-- Modal Actions -->
      <div class="modal-actions">
        <ActionButton
          variant="secondary"
          @click="handleCancel"
          class="modal-button"
          :disabled="loading"
        >
          {{ cancelText }}
        </ActionButton>
        <ActionButton
          variant="primary"
          @click="handleConfirm"
          class="modal-button"
          :loading="loading"
          :disabled="loading"
        >
          {{ confirmText }}
        </ActionButton>
      </div>
    </v-card>
  </v-dialog>
</template>

<script setup>
import { defineProps, defineEmits, computed } from "vue";
import ActionButton from "@/components/common/ActionButton.vue";

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false,
  },
  title: {
    type: String,
    default: "Duplicate Activity",
  },
  description: {
    type: String,
    default:
      "This will create a copy of the activity in the same cart. Are you sure you want to duplicate it?",
  },
  confirmText: {
    type: String,
    default: "Duplicate",
  },
  cancelText: {
    type: String,
    default: "Back",
  },
  loading: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["update:modelValue", "confirm", "cancel"]);

const isOpen = computed({
  get: () => props.modelValue,
  set: (value) => emit("update:modelValue", value),
});

const handleConfirm = () => {
  emit("confirm");
};

const handleCancel = () => {
  emit("cancel");
  isOpen.value = false;
};
</script>

<style scoped>
.confirmation-modal {
  background: white;
  border-radius: 12px;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 32px;
  width: 100%;
  box-shadow: none;
  border: none;
}

/* Fade transition animations - smooth and jitter-free */
.fade-enter-active,
.fade-leave-active {
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
}

.fade-enter-from {
  opacity: 0;
  transform: scale(0.95);
}

.fade-leave-to {
  opacity: 0;
  transform: scale(0.95);
}

.fade-enter-to,
.fade-leave-from {
  opacity: 1;
  transform: scale(1);
}

/* Prevent jittering by ensuring smooth transitions */
:deep(.v-dialog) {
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
}

:deep(.v-overlay__content) {
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
}

/* Ensure modal content doesn't cause layout shifts */
.confirmation-modal {
  will-change: transform, opacity;
  backface-visibility: hidden;
  transform: translateZ(0);
  position: relative;
}

.modal-content {
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;
}

.modal-text {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
}

.modal-title {
  font-family: "League Spartan", sans-serif;
  font-weight: 700;
  font-size: var(--fs-big);
  line-height: 24px;
  color: var(--black-body-text);
  margin: 0;
  letter-spacing: 0.024px;
}

.modal-description {
  font-family: "League Spartan", sans-serif;
  font-weight: 400;
  font-size: var(--fs-medium);
  line-height: 24px;
  color: var(--neutral-700);
  margin: 0;
  letter-spacing: 0.08px;
}

.modal-actions {
  display: flex;
  gap: 8px;
  width: 100%;
}

.modal-button {
  flex: 1;
  height: 56px;
}

/* Responsive Design */
@media (max-width: 768px) {
  .confirmation-modal {
    padding: 20px;
    gap: 24px;
  }

  .modal-actions {
    flex-direction: column-reverse;
    gap: 12px;
  }

  .modal-button {
    width: 100%;
  }

  :deep(.modal-button .action-button__text) {
    font-size: 14px;
  }
}
</style>
